import { useEffect, useLayoutEffect, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import Starfield from '@/components/Starfield';
import SEOHead from '@/components/SEOHead';
import StructuredData from '@/components/StructuredData';
import '@/styles/layout.css';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface LayoutProps {
  children: ReactNode;
  currentPageName: string;
}

// This component ensures pages always start at the top by correcting
// the scroll position before the browser has a chance to paint.
function ScrollManager() {
  const { pathname } = useLocation();

  useIsomorphicLayoutEffect(() => {
    // This prevents the "jump" by running before the browser paints the new screen.
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

export default function Layout({ children, currentPageName: _currentPageName }: LayoutProps) {
  useEffect(() => {
    // Set theme on initial load
    const savedTheme = localStorage.getItem('theme') || 'dark';
    const savedStars = localStorage.getItem('stars-enabled') || 'true';
    
    document.documentElement.setAttribute('data-theme', savedTheme);
    document.documentElement.setAttribute('data-stars', savedStars === 'true' ? 'enabled' : 'disabled');
    
    // Add a class to body to ensure proper styling
    document.body.style.margin = '0';
    document.body.style.padding = '0';
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    <>
      <SEOHead />
      <StructuredData />
      <ScrollManager />
      
      {/* Background Container */}
      <div className="fixed inset-0 -z-[1] pointer-events-none overflow-hidden">
        
        {/* Light mode background - Professional Dot Grid */}
        <div className="light-bg fixed inset-0 bg-[#E3E9FF] from-slate-50 via-blue-50 to-indigo-100">
          <div className="dot-pattern absolute inset-0" />
        </div>

        {/* Static dark background when stars are disabled */}
        <div className="static-dark-bg fixed inset-0 bg-[#0f172a]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        </div>
        
        {/* Dark mode starfield - only visible in dark mode when stars enabled */}
        <Starfield />
      </div>

      {/* Content */}
      <div className="min-h-screen relative z-1 transition-colors duration-300">
        {children}
      </div>
    </>
  );
}

